import React, {forwardRef, useEffect} from "react";
import Editor, {loader} from "@monaco-editor/react";
import {Paper} from "@mui/material";
import {MarkerSeverity} from "monaco-editor";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {CODE_LANGUAGES} from "./constants";
import {languageSelector} from "../../state/app/appSelectors";
import "./style.css";

const getHasErrors = markers => markers.filter(m => m.severity === MarkerSeverity.Error).length > 0;

const CodeEditor = ({value, onChange, codeLanguage}, ref) => {
  const {t} = useTranslation();
  const lang = useSelector(languageSelector);

  useEffect(() => {
    loader.config({
      "vs/nls": {
        availableLanguages: {
          "*": lang
        }
      }
    });

    loader.init();
  }, [lang]);

  const getMarkers = (editor, monaco) => {
    if (!editor || !monaco) {
      return [];
    }

    const model = editor.getModel();
    if (!model) {
      return [];
    }

    return monaco.editor.getModelMarkers({resource: model.uri});
  };

  const handleEditorDidMount = (editor, monaco) => {
    if (!ref.current?.hasErrors) {
      const markers = getMarkers(editor, monaco);
      const hasErrors = getHasErrors(markers);
      ref.current = {
        hasErrors: hasErrors
      };
    }
  };

  const handleEditorValidation = markers => {
    const hasErrors = getHasErrors(markers);
    ref.current = {
      hasErrors: hasErrors
    };
  };

  const handleEditorChange = value => {
    if (onChange) {
      onChange(value);
    }
  };

  return (
    <Paper variant="outlined" className="code-editor-container" sx={{height: "100%"}}>
      <Editor
        language={
          codeLanguage && Object.values(CODE_LANGUAGES).includes(codeLanguage) ? codeLanguage : CODE_LANGUAGES.json
        }
        theme="light"
        onMount={handleEditorDidMount}
        onValidate={handleEditorValidation}
        value={value}
        onChange={handleEditorChange}
        options={{
          fontSize: 14,
          minimap: {
            enabled: false
          },
          scrollBeyondLastLine: false,
          automaticLayout: true,
          contextmenu: false
        }}
        loading={t("components.codeEditor.messages.loading")}
      />
    </Paper>
  );
};

export default forwardRef(CodeEditor);
